import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CircleDollarSign,
  ExternalLink,
  Inbox,
  Loader2,
  Pencil,
  Receipt,
  UserX,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Pill, toneFor } from '@/components/ui/pill';
import { Tag, channelLabel, channelToneFor } from '@/components/ui/tag';
import { EmptyState } from '@/components/ui/empty-state';
import { Pagination } from '@/components/ui/pagination';
import { PageHeader } from '@/components/ui/page-header';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
  DataTableSkeleton,
} from '@/components/ui/data-table';
import { PlatformIcon } from '@/components/PlatformIcon';
import { InviteStagePill } from '@/components/invites/InviteStagePill';
import { OutreacherAccountsSection } from '@/components/outreach-accounts/OutreacherAccountsSection';
import { ContactDetailsForm } from '@/components/ContactDetailsForm';
import {
  useAdminInviterByPublicId,
  useAdminInvites,
  useAdminPayouts,
  useAdminPayroll,
  useAdminTeams,
  useAdminTeamSettings,
  useInviterAnalytics,
  useUpdateInviter,
} from '@/hooks/useAdmin';
import { GLOSSARY, payModelLabel, payoutStatusLabel, providerLabel, teamLabel } from '@/lib/labels';
import { formatDate, formatWeekRange } from '@/lib/dates';
import { formatMoney } from '@/lib/money';
import { formatRate, outreacherFunnel } from '@/lib/peoplePages';
import type { OutreachInviter } from '@/api/types';
import { inviteLink, inviterInvitesLink } from '@/lib/moneyPages';
import { cn } from '@/lib/utils';
import { contactDetailsOf, hasContactDetails, whatsappHref } from '@/lib/contactDetails';

const INVITES_PAGE_SIZE = 25;

/**
 * One outreacher on one page: who they are, what they've logged, what
 * they're owed and what they've been paid. Every number links back to the
 * list it came from.
 */
export function AdminOutreacherProfile(): JSX.Element {
  const { publicId } = useParams<{ publicId: string }>();
  const { inviter, isLoading, error, notFound } = useAdminInviterByPublicId(publicId ?? null);
  const teams = useAdminTeams();

  const back = (
    <Link
      to="/admin/outreachers"
      className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-3 w-3" /> Outreachers
    </Link>
  );

  if (isLoading) {
    return (
      <div className="space-y-5">
        {back}
        <p className="flex items-center text-sm text-muted-foreground" aria-live="polite">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading outreacher…
        </p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-5">
        {back}
        <p role="alert" className="text-sm text-destructive">
          Couldn't load this outreacher: {error.message}
        </p>
      </div>
    );
  }
  if (notFound || !inviter) {
    return (
      <div className="space-y-5">
        {back}
        <EmptyState
          icon={<UserX className="h-6 w-6" />}
          title="No outreacher with this link."
          hint="They may have been removed, or the link is wrong. Find them on the Outreachers list."
        />
      </div>
    );
  }

  const teamName = teamLabel(
    inviter.team,
    (teams.data ?? []).find((t) => t.code === inviter.team)?.displayName,
  );
  const editHref = `/admin/outreachers?${new URLSearchParams({
    q: inviter.name,
    edit: inviter.publicId,
    ...(inviter.isActive ? {} : { inactive: '1' }),
  }).toString()}`;

  return (
    <div className="space-y-6">
      {back}
      <PageHeader
        title={inviter.name}
        description={inviter.email ?? undefined}
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to={editHref}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={inviterInvitesLink(inviter.id)}>
                <ExternalLink className="h-3.5 w-3.5" /> View their invites
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={`/admin/payouts?inviter=${inviter.id}`}>
                <Receipt className="h-3.5 w-3.5" /> Payouts
              </Link>
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-1.5">
          <Tag>{teamName}</Tag>
          <Tag icon={false}>
            {payModelLabel(inviter.compensationModel)}
            {inviter.compensationModel === 'RETAINER' && inviter.retainerAmount
              ? ` · $${formatMoney(inviter.retainerAmount)}/week`
              : ''}
          </Tag>
          <Pill tone={inviter.clerkUserId ? 'paid' : 'muted'} title="Outreacher portal login">
            {inviter.clerkUserId ? 'Login: Connected' : 'Login: Not signed up'}
          </Pill>
          <Pill tone={inviter.isActive ? 'paid' : 'muted'}>
            {inviter.isActive ? 'Active' : 'Inactive'}
          </Pill>
          <span className="text-xs text-muted-foreground">
            Added {formatDate(inviter.createdAt)}
          </span>
        </div>
      </PageHeader>

      <Overview inviter={inviter} />
      <ContactDetails inviter={inviter} />
      <BulkImportAccess inviter={inviter} teamName={teamName} />
      <OutreacherAccountsSection inviter={inviter} />
      <RecentInvites inviter={inviter} />
      <Payouts inviter={inviter} />
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────

function Overview({ inviter }: { inviter: OutreachInviter }): JSX.Element {
  const analytics = useInviterAnalytics(inviter.publicId);
  const payroll = useAdminPayroll();
  const row = (payroll.data?.inviters ?? []).find((r) => r.inviterId === inviter.id) ?? null;
  const funnel = useMemo(
    () => outreacherFunnel(analytics.data?.statusBreakdown ?? []),
    [analytics.data],
  );

  const payrollHint = payroll.isLoading
    ? 'Loading…'
    : payroll.error
      ? "Couldn't load payroll."
      : !row
        ? inviter.isActive
          ? 'Not on this period’s payroll.'
          : 'Inactive outreachers are not on Payroll.'
        : undefined;

  return (
    <section aria-labelledby="overview-h" className="space-y-3">
      <SectionTitle id="overview-h">Overview</SectionTitle>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat
          label="Owed (before adjustments)"
          title={GLOSSARY.owedBeforeAdjustments}
          value={row ? `$${formatMoney(row.owed)}` : '—'}
          hint={payrollHint}
        />
        <Stat
          label="Paid this period"
          value={row ? `$${formatMoney(row.paidThisPeriod)}` : '—'}
          hint={
            row && payroll.data
              ? formatWeekRange(payroll.data.periodStart)
              : payrollHint
          }
        />
        <Stat
          label="Invites logged"
          title={GLOSSARY.invite}
          value={analytics.isLoading ? '…' : funnel.logged.toLocaleString('en-US')}
          hint="All time"
          to={inviterInvitesLink(inviter.id)}
        />
        <Stat
          label="Joined"
          value={analytics.isLoading ? '…' : funnel.joined.toLocaleString('en-US')}
          hint={
            funnel.logged > 0
              ? `${formatRate(funnel.joinRate)} of ${funnel.logged.toLocaleString('en-US')} logged`
              : 'Nothing logged yet'
          }
        />
        <Stat
          label="Posted"
          value={analytics.isLoading ? '…' : funnel.posted.toLocaleString('en-US')}
          hint={
            funnel.joined > 0
              ? `${formatRate(funnel.postRate)} of ${funnel.joined.toLocaleString('en-US')} joined`
              : 'No one has joined yet'
          }
        />
      </div>
      {analytics.error && (
        <p role="alert" className="text-xs text-destructive">
          Couldn't load invite counts: {(analytics.error as Error).message}
        </p>
      )}
    </section>
  );
}

function Stat({
  label,
  value,
  hint,
  title,
  to,
}: {
  label: string;
  value: string;
  hint?: string;
  title?: string;
  to?: string;
}): JSX.Element {
  const body = (
    <>
      <p className="text-xs text-muted-foreground" title={title}>
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </>
  );
  return to ? (
    <Link to={to} className="rounded-md border bg-card p-4 transition-colors hover:bg-accent/40">
      {body}
    </Link>
  ) : (
    <div className="rounded-md border bg-card p-4">{body}</div>
  );
}

function SectionTitle({ id, children, actions }: { id: string; children: ReactNode; actions?: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 id={id} className="text-lg font-medium">
        {children}
      </h2>
      {actions}
    </div>
  );
}

// ── Contact details ───────────────────────────────────────────────────────

/** How to reach this outreacher. They fill it in on My details; admins can edit it here. */
function ContactDetails({ inviter }: { inviter: OutreachInviter }): JSX.Element {
  const update = useUpdateInviter();
  const value = useMemo(() => contactDetailsOf(inviter), [inviter]);
  const chat = whatsappHref(inviter.contactWhatsapp);
  return (
    <section aria-labelledby="contact-h" className="space-y-3">
      <SectionTitle
        id="contact-h"
        actions={
          chat && (
            <Button asChild variant="outline" size="sm">
              <a href={chat} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5" /> Message on WhatsApp
              </a>
            </Button>
          )
        }
      >
        Contact details
      </SectionTitle>
      {!hasContactDetails(inviter) && (
        <p className="text-xs text-muted-foreground">
          {inviter.name} hasn't added any yet. They can from My details in their portal, or add them here.
        </p>
      )}
      <ContactDetailsForm
        value={value}
        pending={update.isPending}
        onSave={(patch) =>
          update.mutateAsync({ id: inviter.id, body: patch }).then(
            () => toast.success(`${inviter.name}: contact details saved`),
            (err: Error) => toast.error(`Couldn't save contact details for ${inviter.name}: ${err.message}`),
          )
        }
      />
    </section>
  );
}

// ── Bulk import access ────────────────────────────────────────────────────

type BulkChoice = 'team' | 'on' | 'off';

const choiceOf = (v: boolean | null | undefined): BulkChoice => (v === true ? 'on' : v === false ? 'off' : 'team');
const valueOf = (c: BulkChoice): boolean | null => (c === 'on' ? true : c === 'off' ? false : null);

/**
 * Per-outreacher override of the team's bulk import rule: follow the team,
 * always allow, or never allow (null / true / false on the inviter).
 */
function BulkImportAccess({ inviter, teamName }: { inviter: OutreachInviter; teamName: string }): JSX.Element {
  const settings = useAdminTeamSettings();
  const update = useUpdateInviter();
  const [pending, setPending] = useState<BulkChoice | null>(null);
  const teamOn = (settings.data ?? []).find((s) => s.team === inviter.team)?.bulkImportEnabled === true;
  const current = choiceOf(inviter.bulkImportEnabled);
  const effective = current === 'team' ? teamOn : current === 'on';

  const options: Array<{ key: BulkChoice; label: string }> = [
    { key: 'team', label: `Follow team (${settings.isLoading ? '…' : teamOn ? 'On' : 'Off'})` },
    { key: 'on', label: 'Allowed' },
    { key: 'off', label: 'Not allowed' },
  ];

  const describe = (c: BulkChoice): string =>
    c === 'team'
      ? `${inviter.name} will follow ${teamName}'s rule, which is ${teamOn ? 'on' : 'off'}. Changing the team rule on Team rules will change this too.`
      : c === 'on'
        ? `${inviter.name} can bulk import, even if ${teamName}'s rule is off.`
        : `${inviter.name} can't bulk import, even if ${teamName}'s rule allows it.`;

  async function save(c: BulkChoice): Promise<void> {
    try {
      await update.mutateAsync({ id: inviter.id, body: { bulkImportEnabled: valueOf(c) } });
      toast.success(`${inviter.name}: bulk import ${c === 'team' ? 'follows the team' : c === 'on' ? 'allowed' : 'not allowed'}`);
    } catch (err) {
      toast.error(`Couldn't save bulk import for ${inviter.name}: ${(err as Error).message}`);
    }
  }

  return (
    <section aria-labelledby="bulk-h" className="space-y-3">
      <SectionTitle id="bulk-h">Bulk import</SectionTitle>
      <div className="panel flex flex-wrap items-center justify-between gap-4 rounded-xl p-4">
        <div className="min-w-0 space-y-1">
          <p className="text-sm">
            Upload a CSV or Excel list and mark everyone messaged or log replies at once.{' '}
            <span className="font-semibold">{effective ? 'Allowed' : 'Not allowed'}</span>
            <span className="text-muted-foreground"> right now.</span>
          </p>
          <p className="text-xs text-muted-foreground">Overrides {teamName}&apos;s rule for this outreacher only.</p>
        </div>
        <div role="radiogroup" aria-label="Bulk import for this outreacher" className="inline-flex flex-wrap rounded-lg border border-input bg-[var(--input-bg)] p-0.5">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={current === o.key}
              disabled={update.isPending}
              onClick={() => current !== o.key && setPending(o.key)}
              className={cn(
                'h-8 rounded-md px-3 text-xs font-semibold transition-colors pointer-coarse:h-10 disabled:opacity-60',
                current === o.key ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
        title={`Change bulk import for ${inviter.name}?`}
        description={pending ? describe(pending) : undefined}
        confirmLabel="Save"
        pending={update.isPending}
        onConfirm={() => (pending ? save(pending) : undefined)}
      />
    </section>
  );
}

// ── Recent invites ────────────────────────────────────────────────────────

function RecentInvites({ inviter }: { inviter: OutreachInviter }): JSX.Element {
  const [page, setPage] = useState(1);
  const invites = useAdminInvites({ inviterId: inviter.id, page, limit: INVITES_PAGE_SIZE });
  const settings = useAdminTeamSettings();
  const teamTracks = (settings.data ?? []).find((s) => s.team === inviter.team)?.clippingResolveRequired;
  const rows = invites.data?.data ?? [];
  const total = invites.data?.meta.total ?? 0;

  return (
    <section aria-labelledby="invites-h" className="space-y-3">
      <SectionTitle
        id="invites-h"
        actions={
          <Link
            to={inviterInvitesLink(inviter.id)}
            className="text-xs text-muted-foreground hover:text-foreground hover:underline"
          >
            Open in Invites
          </Link>
        }
      >
        Recent invites
      </SectionTitle>
      {invites.error ? (
        <p role="alert" className="text-sm text-destructive">
          Couldn't load invites: {(invites.error as Error).message}
        </p>
      ) : !invites.isLoading && rows.length === 0 ? (
        <EmptyState
          icon={<Inbox className="h-6 w-6" />}
          title="No invites logged yet."
          hint="Invites appear here once this outreacher logs a creator who replied."
        />
      ) : (
        <>
          <DataTable aria-label="Recent invites">
            <DataTableHeader>
              <tr>
                <DataTableHeaderCell>Handle</DataTableHeaderCell>
                <DataTableHeaderCell>Channel</DataTableHeaderCell>
                <DataTableHeaderCell>Stage</DataTableHeaderCell>
                <DataTableHeaderCell numeric>Earned</DataTableHeaderCell>
                <DataTableHeaderCell>Logged</DataTableHeaderCell>
              </tr>
            </DataTableHeader>
            {invites.isLoading ? (
              <DataTableSkeleton rows={5} cols={['w-32', 'w-8', 'w-20', 'w-14', 'w-20']} />
            ) : (
              <tbody>
                {rows.map((inv) => (
                  <DataTableRow key={inv.id} state={inv.deletedAt ? 'terminal' : 'default'}>
                    <DataTableCell anchor>
                      <div className="flex items-center gap-2">
                        <PlatformIcon platform={inv.channel} size={14} className="text-muted-foreground" />
                        <Link
                          to={inviteLink({ handle: inv.targetIdentifier, openPublicId: inv.publicId, inviterId: inv.inviterId })}
                          className="hover:underline"
                        >
                          @{inv.targetIdentifier}
                        </Link>
                      </div>
                    </DataTableCell>
                    <DataTableCell data-label="Channel">
                      <Tag tone={channelToneFor(inv.channel)}>{channelLabel(inv.channel)}</Tag>
                    </DataTableCell>
                    <DataTableCell data-label="Stage">
                      <InviteStagePill invite={inv} teamTracks={teamTracks} />
                    </DataTableCell>
                    <DataTableCell numeric data-label="Earned">
                      {inv.lockedValue ? `$${formatMoney(inv.lockedValue)}` : '—'}
                    </DataTableCell>
                    <DataTableCell muted data-label="Logged">
                      {formatDate(inv.createdAt)}
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </tbody>
            )}
          </DataTable>
          <Pagination page={page} limit={INVITES_PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}
    </section>
  );
}

// ── Payouts ───────────────────────────────────────────────────────────────

function Payouts({ inviter }: { inviter: OutreachInviter }): JSX.Element {
  const payouts = useAdminPayouts({ inviterId: inviter.id, limit: 50 });
  const rows = payouts.data?.data ?? [];

  return (
    <section aria-labelledby="payouts-h" className="space-y-3">
      <SectionTitle
        id="payouts-h"
        actions={
          <Link
            to={`/admin/payouts?inviter=${inviter.id}`}
            className="text-xs text-muted-foreground hover:text-foreground hover:underline"
          >
            Open in Payouts
          </Link>
        }
      >
        Payouts
      </SectionTitle>
      {payouts.error ? (
        <p role="alert" className="text-sm text-destructive">
          Couldn't load payouts: {(payouts.error as Error).message}
        </p>
      ) : !payouts.isLoading && rows.length === 0 ? (
        <EmptyState
          icon={<CircleDollarSign className="h-6 w-6" />}
          title="No payouts yet."
          hint="Payouts appear here once they're paid from Payroll."
        />
      ) : (
        <DataTable aria-label="Payouts">
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell>Week</DataTableHeaderCell>
              <DataTableHeaderCell>Status</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Amount</DataTableHeaderCell>
              <DataTableHeaderCell>Paid through</DataTableHeaderCell>
              <DataTableHeaderCell>Paid on</DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          {payouts.isLoading ? (
            <DataTableSkeleton rows={3} cols={['w-40', 'w-20', 'w-14', 'w-24', 'w-20']} />
          ) : (
            <tbody>
              {rows.map((p) => (
                <DataTableRow key={p.id}>
                  <DataTableCell anchor>
                    <div className="flex items-center gap-2">
                      <CircleDollarSign className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      {formatWeekRange(p.payrollWeekOf)}
                    </div>
                  </DataTableCell>
                  <DataTableCell data-label="Status">
                    <Pill tone={toneFor(p.status)}>{payoutStatusLabel(p.status)}</Pill>
                  </DataTableCell>
                  <DataTableCell numeric data-label="Amount">
                    ${formatMoney(p.totalAmount)}
                  </DataTableCell>
                  <DataTableCell muted data-label="Paid through">
                    {providerLabel(p.providerKey) || '—'}
                  </DataTableCell>
                  <DataTableCell muted data-label="Paid on">
                    {formatDate(p.paidAt)}
                  </DataTableCell>
                </DataTableRow>
              ))}
            </tbody>
          )}
        </DataTable>
      )}
    </section>
  );
}
